import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useArtistAuth } from '../../hooks/useArtistAuth';
import { 
  Calendar, 
  DollarSign, 
  MapPin, 
  Clock, 
  ArrowLeft, 
  Plus,
  Eye,
  FileText,
  Building2,
  BookOpen
} from 'lucide-react';
import LoadingSpinner from '../ui/LoadingSpinner';
import '../../styles/SubmissionSystem.css';

const SubmissionDetailsPage = () => {
  const { submissionId } = useParams();
  const { user, isArtist, loading: authLoading } = useArtistAuth();
  const [submission, setSubmission] = useState(null);
  const [collection, setCollection] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to detect and render HTML content
  const renderHTMLContent = (content, className = 'html-content') => {
    if (!content) return null;
    
    // Check if the content contains HTML tags
    const htmlRegex = /<[^>]*>/;
    const isHTML = htmlRegex.test(content);
    
    if (isHTML) {
      return (
        <div 
          className={className} 
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else {
      return <p className={className}>{content}</p>;
    }
  };

  useEffect(() => {
    fetchSubmissionDetails();
  }, [submissionId]);

  const fetchSubmissionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch submission details
      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions_details')
        .select('*')
        .eq('submission_id', submissionId)
        .single();

      if (submissionError) {
        console.error('Error fetching submission:', submissionError);
        setError('Submission not found');
        return;
      }

      setSubmission(submissionData);

      // Fetch collection details if collection_id exists
      if (submissionData.collection_id) {
        try {
          const { data: collectionData } = await supabase
            .from('collections')
            .select('title, description')
            .eq('collection_id', submissionData.collection_id)
            .single();
          setCollection(collectionData);
        } catch (collectionError) {
          console.warn('Could not fetch collection details:', collectionError);
        }
      }

      // Get gallery information
      if (submissionData.gallery_id) {
        const { data: galleryData } = await supabase
          .from('galleries')
          .select('name, address, website, contact_email')
          .eq('gallery_id', submissionData.gallery_id)
          .single();
        setGallery(galleryData);
      }

    } catch (error) {
      console.error('Error:', error);
      setError('Failed to load submission details');
    } finally {
      setLoading(false);
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return 'No deadline';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Deadline passed';
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    if (days <= 7) return `Due in ${days} days`;
    
    return new Date(deadline).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDeadlineClass = (deadline) => {
    if (!deadline) return '';
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'urgent';
    if (days <= 3) return 'urgent';
    if (days <= 7) return 'warning';
    
    return '';
  };

  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return 0;
    
    const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
    return days < 0 ? 0 : days;
  };

  if (loading) {
    return (
      <div className="submission-loading-container">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="submission-system-container">
        <div className="submission-empty-state">
          <div className="submission-empty-icon">
            <FileText className="w-16 h-16" />
          </div>
          <h2 className="submission-empty-title">Submission Not Found</h2>
          <p className="submission-empty-description">
            {error || 'The submission you\'re looking for doesn\'t exist or you don\'t have access to it.'}
          </p>
          <Link to="/submissions" className="submission-btn submission-btn-primary">
            Back to Submissions
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="submission-system-container">
      {/* Header */}
      <div className="submission-header">
        <div className="submission-header-content">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="submission-title">{submission.title}</h1>
                <p className="submission-subtitle">Submission Details & Guidelines</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {authLoading ? (
                <div className="submission-btn submission-btn-primary" disabled>
                  <LoadingSpinner />
                  Loading...
                </div>
              ) : user && isArtist ? (
                <Link
                  to={`/submissions/${submissionId}/apply`}
                  className="submission-btn submission-btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Apply Now
                </Link>
              ) : (
                <Link
                  to={`/login?from=${encodeURIComponent(`/submissions/${submissionId}/apply`)}`}
                  className="submission-btn submission-btn-primary"
                >
                  <Plus className="w-4 h-4" />
                  Sign In to Apply
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="submission-main-content">
        <div className="submission-grid submission-grid-2">
          {/* Main Content */}
          <div className="submission-content-card">
            <div className="submission-content-card-header">
              <h2 className="submission-content-card-title">Submission Overview</h2>
            </div>
            <div className="submission-content-card-body">
              {/* Cover Image */}
              {submission.cover_image && (
                <div className="submission-cover-image">
                  <img 
                    src={submission.cover_image} 
                    alt={submission.title}
                    className="cover-image"
                  />
                </div>
              )}

              {/* Theme/Description */}
              {submission.theme && (
                <div className="submission-theme-section">
                  <h3 className="submission-section-title">About the Show</h3>
                  {renderHTMLContent(submission.theme, 'submission-theme-content')}
                </div>
              )}

              {/* Guidelines */}
              {submission.submission_guidelines && (
                <div className="submission-guidelines-section">
                  <h3 className="submission-section-title">Submission Guidelines</h3>
                  {renderHTMLContent(submission.submission_guidelines, 'submission-guidelines-content')}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="submission-content-card">
            <div className="submission-content-card-header">
              <h2 className="submission-content-card-title">Submission Details</h2>
            </div>
            <div className="submission-content-card-body">
              <div className="submission-details-grid">
                {/* Price */}
                <div className="submission-detail-item">
                  <div className="submission-detail-icon">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <div className="submission-detail-content">
                    <span className="submission-detail-label">Submission Fee</span>
                    <span className="submission-detail-value">${submission.submission_price}</span>
                  </div>
                </div>

                {/* Deadline */}
                <div className="submission-detail-item">
                  <div className="submission-detail-icon">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div className="submission-detail-content">
                    <span className="submission-detail-label">Deadline</span>
                    <span className="submission-detail-value">
                      {formatDeadline(submission.submission_deadline)}
                    </span>
                  </div>
                </div>

                {/* Days Left */}
                {submission.submission_deadline && (
                  <div className="submission-detail-item">
                    <div className="submission-detail-icon">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div className="submission-detail-content">
                      <span className="submission-detail-label">Time Remaining</span>
                      <span className={`submission-detail-value ${getDeadlineClass(submission.submission_deadline)}`}>
                        {getDaysUntilDeadline(submission.submission_deadline)} days left
                      </span>
                    </div>
                  </div>
                )}

                {/* Gallery */}
                {gallery && (
                  <div className="submission-detail-item">
                    <div className="submission-detail-icon">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <div className="submission-detail-content">
                      <span className="submission-detail-label">Gallery</span>
                      <span className="submission-detail-value">{gallery.name}</span>
                      {gallery.location && (
                        <p className="submission-detail-description">{gallery.location}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Apply Button */}
              <div className="submission-apply-section">
                {authLoading ? (
                  <div className="submission-btn submission-btn-primary w-full text-center" disabled>
                    <LoadingSpinner />
                    Loading...
                  </div>
                ) : user && isArtist ? (
                  <Link
                    to={`/submissions/${submissionId}/apply`}
                    className="submission-btn submission-btn-primary w-full text-center"
                  >
                    <Plus className="w-4 h-4" />
                    Apply Now
                  </Link>
                ) : (
                  <Link
                    to={`/login?from=${encodeURIComponent(`/submissions/${submissionId}/apply`)}`}
                    className="submission-btn submission-btn-primary w-full text-center"
                  >
                    <Plus className="w-4 h-4" />
                    Sign In to Apply
                  </Link>
                )}
                <p className="submission-apply-note">
                  {user && isArtist ? 'Start your application process' : 'Sign in to start your application process'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetailsPage;
